import { css } from '@emotion/react';
import { Box, Flex, Stack, Text, Title, desktopFirstMediaQuery } from '@inflearn/ds-react-next';
import { Trans, useTranslation } from 'next-i18next';

import { NEXT_PUBLIC_CDN_URL } from '../../../../appConfig';
import StartInstructorButton from './StartInstructorButton';
import { JOIN_INSTRUCTOR_SPOTS } from './constants/gtmEventParams';

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styleSection}>
      <Flex
        direction="column"
        align="center"
        gap={48}
        styles={{
          root: styleContainer,
        }}
      >
        <Stack
          gap={16}
          styles={{
            root: styleTextStack,
          }}
        >
          <Text
            c="gray.9"
            fw={700}
            lh={1.3}
            styles={{
              root: styleHeading,
            }}
          >
            <Trans
              i18nKey="open.knowledge.hero.heading"
              components={{
                green: <span css={styleGreenText} />,
                br: <br />,
              }}
            />
          </Text>
          <Stack>
            <Title
              order={3}
              fw={700}
              lh={1.5}
              c="dark.9"
              styles={{
                root: styleSubBold,
              }}
            >
              {t('open.knowledge.hero.subtitle')}
            </Title>
            <Box css={styleSubText}>
              <Text
                c="gray.9"
                styles={{
                  root: styleSubNormal,
                }}
              >
                <Trans
                  i18nKey="open.knowledge.hero.description"
                  components={{ br: <br css={styleMobileOnlyBr} /> }}
                />
              </Text>
            </Box>
          </Stack>
        </Stack>
        <Stack gap={12} align="center">
          <StartInstructorButton
            spot={JOIN_INSTRUCTOR_SPOTS.HERO}
            variant="filled"
            color="infgreen.7"
            miw={260}
            h={56}
          >
            {t('open.knowledge.cta.start')}
          </StartInstructorButton>
          <Text c="gray.7" styles={{ root: styleCtaNote }}>
            {t('open.knowledge.hero.cta.note')}
          </Text>
        </Stack>
      </Flex>
    </Box>
  );
};

export default HeroSection;

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: '100%',
  paddingTop: 200,
  paddingBottom: 200,
  backgroundImage: `url("${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/title_bg_lg.png")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  [desktopFirstMediaQuery.tablet]: {
    paddingTop: 160,
    paddingBottom: 160,
  },
  [desktopFirstMediaQuery.mobile]: {
    backgroundImage: `url("${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/title_bg_sm.png")`,
    paddingTop: 120,
    paddingBottom: 120,
  },
});

const styleContainer = css({
  position: 'relative',
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleTextStack = css({
  width: '100%',
  textAlign: 'center',
});

const styleHeading = css({
  fontSize: 56,
  fontWeight: 700,
  lineHeight: 1.3,
  wordBreak: 'keep-all',
  [desktopFirstMediaQuery.tablet]: {
    fontSize: 44,
  },
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 28,
  },
});

const styleGreenText = css({
  color: '#00a760',
});

const styleSubText = css({
  textAlign: 'center',
  fontSize: 22,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleSubBold = css({
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleSubNormal = css({
  fontWeight: 400,
  fontSize: 22,
  lineHeight: 1.5,
  wordBreak: 'keep-all',
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleMobileOnlyBr = css({
  display: 'none',
  [desktopFirstMediaQuery.mobile]: {
    display: 'inline',
  },
});

const styleCtaNote = css({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  textAlign: 'center',
  wordBreak: 'keep-all',
});
